import React from 'react';
import useSystemSettings from '../hooks/useSystemSettings';

const MaintenanceMode: React.FC = () => {
  const { isMaintenanceMode, maintenanceMessage, loading } = useSystemSettings();

  // Don't paint a full-screen loader while the settings fetch is in flight.
  // The rest of the app (and its own Suspense/page spinner) renders immediately;
  // if the system is actually in maintenance, this component swaps in the
  // maintenance UI as soon as the fetch resolves. Mirrors the silent loading
  // pattern used by MetaTagsManager.
  if (loading || !isMaintenanceMode) {
    return null;
  }

  return (
    <div className="min-h-screen bg-brand-cream/40 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-card shadow-lg border border-brand-line p-8 text-center">
        {/* Maintenance Icon */}
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
        </div>

        {/* Maintenance Title */}
        <h1 className="text-2xl font-bold text-brand-ink mb-4">
          Under Maintenance
        </h1>

        {/* Maintenance Message */}
        <p className="text-brand-ink/70 mb-6 leading-relaxed">
          {maintenanceMessage}
        </p>

        {/* Additional Information */}
        <div className="bg-brand-blue/10 border border-brand-blue/20 rounded-card p-4 mb-6">
          <p className="text-sm text-brand-ink/80">
            We're working hard to improve your experience. We'll be back shortly!
          </p>
        </div>

        {/* Contact Information */}
        <div className="text-sm text-brand-ink/60">
          <p>If you need immediate assistance, please contact our support team.</p>
        </div>

        {/* Refresh Button */}
        <button
          onClick={() => window.location.reload()}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-semibold rounded-button text-white bg-brand-orange hover:bg-brand-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange transition-colors"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Check Again
        </button>
      </div>
    </div>
  );
};

export default MaintenanceMode;
